import { useModalStore } from "@/zustand-hooks/modal";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Image from "next/image";
import { crypto_images } from "../crypto_images";
import { CheckCircleIcon, CreditCard, Loader2Icon, MessageCircleIcon, TimerIcon, XCircleIcon } from "lucide-react";
import { Link } from "@/i18n/routing";
import { Button } from '@/components/ui/button';
import { useTranslations } from "next-intl";
import { Transaction } from "@/types/transaction-type";
import useIsMounted from "@/hooks/useIsMounted";
import { formatTransactionFeeDisplay } from "@/utils/formatTransactionFee";

const TransactionModal = ({ is_crypto_transaction = false }: { is_crypto_transaction?: boolean }) => {
  const isMounted = useIsMounted()

  const isOpen = useModalStore((state) => state.isOpen);
  const toggleOpen = useModalStore((state) => state.toggleIsOpen);
  const transaction = useModalStore((state) => state.transaction);
  const infoKeys: (keyof Transaction)[] | [] = transaction ? Object.keys(transaction) : [];
  const table_titles = useTranslations('table_titles');
  const transaction_types = useTranslations('transaction_types');
  const transaction_statuses = useTranslations('transaction_statuses');
  const transaction_buttons = useTranslations('buttons');
  const card_info = useTranslations('card_information');


  if (!isMounted) return null


  if (transaction) return (
    <Dialog open={isOpen} onOpenChange={toggleOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{table_titles('transaction_details')} #{transaction.id}</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <div className="flex even:bg-primary/5 px-2 border-b-2 py-2 [&>span]:flex-1">
            <span className="font-semibold">ID</span>
            <span className="font-semibold">#{transaction.id}</span>
          </div>
          {infoKeys.map(
            (key) =>
              !/(sub_type|is_real|is_sync|hidden|id|card_number|card_holder|bank_name|expiry_date|comment|fee|fee_pct)/g.test(key) && (
                <div
                  key={key}
                  className="flex even:bg-primary/5 px-2 border-b-2 py-2 [&>span]:flex-1"
                >
                  <span className="capitalize">{table_titles(key)}</span>

                  {/(base_asset|quote_asset)/g.test(key) && (
                    <div className="w-1/2 flex items-center gap-2">
                      {/* @ts-ignore */}
                      {transaction[key] ?
                        <>
                          <Image
                            // @ts-ignore
                            src={crypto_images[String(transaction[key]).toLowerCase()]}
                            width={30}
                            height={30}
                            // @ts-ignore
                            alt={`${key === 'quote_asset' ? 'Quote' : 'Base'} Asset: ${transaction[key]}`}
                          />
                          {/* @ts-ignore */}
                          {transaction[key]}
                        </>
                        : '-'
                      }
                    </div>
                  )}

                  {key === 'type' && <div className="w-1/2"><span className="capitalize">{transaction_types(transaction[key])}</span></div>}
                  {key === 'created_at' && <p className="w-1/2">{new Date(transaction[key]).toLocaleDateString()} <span className="text-gray-500 text-xs">{new Date(transaction[key]).toLocaleTimeString()}</span></p>}

                  {key === 'status' && transaction[key].toLowerCase() === 'pending' && <div className="w-1/2"><p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-surface border border-surface-border text-surface-foreground"><TimerIcon width={15} className="shrink-0" />{transaction_statuses(transaction[key])}</p></div>}
                  {key === 'status' && transaction[key].toLowerCase() === 'executed' && <div className="w-1/2"><p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-emerald-500 text-white"><CheckCircleIcon width={15} className="shrink-0" /> {transaction_statuses(transaction[key])}</p></div>}
                  {key === 'status' && transaction[key].toLowerCase() === 'declined' && <div className="w-1/2"><p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-red-500 text-white"><XCircleIcon width={15} className="shrink-0" />{transaction_statuses(transaction[key])}</p></div>}
                  {key === 'status' && transaction[key].toLowerCase() === "processing" && <div className="w-1/2"><p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-yellow-500 text-white"><Loader2Icon width={15} className="shrink-0 animate-spin" />{transaction_statuses(transaction[key])}</p></div>}

                  {key === 'status' && transaction[key].toLowerCase() === 'blocked' && <div className="w-1/2"><p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-red-700 text-white"><XCircleIcon width={15} className="shrink-0" />{transaction_statuses(transaction[key])}</p></div>}
                  {/* @ts-ignore Тут я вывожу все остальные ключи*/}
                  {!/(base_asset|quote_asset|type|status|created_at|comment)/g.test(key) && (
                    <div className="w-1/2">
                      {/* @ts-ignore */}
                      {transaction[key]}
                    </div>
                  )}


                </div>
              )
          )}

          <div className="flex even:bg-primary/5 px-2 border-b-2 py-2 [&>span]:flex-1">
            <span className="font-semibold">{table_titles("fee")}</span>
            <div className="w-1/2">
              <span>{formatTransactionFeeDisplay(transaction)}</span>
            </div>
          </div>


          {/* Информация о карте, если есть */}
          {(transaction.card_number || transaction.card_holder || transaction.bank_name) && (
            <div className="mt-4 p-3 bg-brand-500/10 dark:bg-brand-500/10 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-brand-600" />
                <span className="font-semibold text-brand-600">{card_info('title')}</span>
              </div>

              {transaction.card_number && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{card_info('card_number')}:</span>
                  <span className="font-mono font-semibold">{transaction.card_number}</span>
                </div>
              )}

              {transaction.card_holder && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{card_info('card_holder')}:</span>
                  <span className="font-semibold">{transaction.card_holder}</span>
                </div>
              )}

              {transaction.expiry_date && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{card_info('expiry_date')}:</span>
                  <span className="font-mono font-semibold">{transaction.expiry_date}</span>
                </div>
              )}

              {transaction.bank_name && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{card_info('bank_name')}:</span>
                  <span className="font-semibold">{transaction.bank_name}</span>
                </div>
              )}
            </div>
          )}

          {
            transaction.comment && transaction.comment.length > 0 &&
            <div className="flex items-start gap-2 even:bg-primary/5 px-2 py-2 border-b-2">
              <span className="font-semibold">
                <MessageCircleIcon className="bg-yellow-400 dark:text-black p-2 box-content rounded-full" />
              </span>
              <div className="w-full bg-surface border border-surface-border rounded-2xl p-2 text-surface-foreground">{transaction.comment}</div>
            </div>
          }

          {
            // @ts-ignore
            is_crypto_transaction && transaction.status.toLowerCase() === 'pending' ? <Link href={`/deposit/crypto/${transaction.id}`}><Button className="w-full mt-4" onClick={() => toggleOpen(!isOpen)}>{transaction_buttons('see_details')}</Button></Link> : null
          }
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionModal;
